import { logUsage } from 'owa-analytics-actions';
import Getter from './types/Getter';
import LazyModule from './LazyModule';
import createQuickPromise from './utils/createQuickPromise';
import markImportAsLoaded from './mutators/markImportAsLoaded';
import isImportLoaded from './selectors/isImportLoaded';
import createImportId from './utils/createImportId';

const FailureDatapointName = 'LazyImportFailure';

export default class LazyImport<TImport, TModule> {
    private id: string;
    private importPromise: Promise<TImport> | undefined;
    private importValue: TImport | undefined;
    private pendingImports = 0;

    constructor(private lazyModule: LazyModule<TModule>, private getter: Getter<TImport, TModule>) {
        // Each LazyImport has a unique ID
        this.id = createImportId();
    }

    import() {
        if (!this.importPromise) {
            this.importPromise = this.lazyModule
                .import()
                .then(module => {
                    // Get the value out of the module
                    this.importValue = this.getter(module);

                    // Once the import is loaded, we return a synchronous promise for faster access
                    this.importPromise = createQuickPromise(this.importValue);

                    // Update the loaded imports map in the store
                    markImportAsLoaded(this.id);

                    this.pendingImports = 0;
                    return this.importValue;
                })
                .catch(error => {
                    // Reset the promise so we'll try to load it again
                    this.importPromise = undefined;

                    // Log some info about the failure
                    logUsage(FailureDatapointName, {
                        message: error.message,
                        pendingImports: this.pendingImports,
                    });

                    this.pendingImports = 0;
                    throw error;
                });
        }

        this.pendingImports++;
        return this.importPromise;
    }

    // Don't use this.  Pretend you didn't even see it.
    dangerouslyImportSync() {
        if (!this.isLoaded()) {
            throw new Error('Import is not available yet.');
        }

        return this.importValue;
    }

    tryImportForRender() {
        // Check if we already have the value.  It's important that we check
        // the loaded state via the store -- that way, if this method is called
        // during render(), the component will get rerendered once the import
        // is loaded.
        if (isImportLoaded(this.id)) {
            return this.importValue;
        }

        // Kick off the import so that it will be available eventually
        this.import();
        return undefined;
    }

    isLoaded(): boolean {
        // Check whether we have ever loaded this import during this session.
        return isImportLoaded(this.id);
    }
}
